import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, BookOpen, Copy as CloneIcon, Loader2 } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { useAuth } from '@/lib/auth-store';
import { api, ApiError, type TemplateSummary } from '@/lib/api';
import { meetsMinRole } from '@/lib/roles';

/**
 * Phase-7 Template Library at `/templates`. Public list of every course
 * with `published_as_template = 1` AND `status = 'active'`. Authenticated
 * instructors see a "Clone to my workspace" button per template; cloning
 * deep-copies the course (modules / items / blocks) under fresh IDs into
 * the cloner's org and routes them to the new course's edit page.
 *
 * Why this exists: solo teachers don't want to start from a blank page.
 * The library lets a community share course skeletons + full content
 * for others to fork. Master plan calls for "Template Library + deep-
 * clone (recursive ID remap)" — the recursive ID remap lives server-
 * side in `apps/api/src/templates/service.ts`.
 *
 * Public page styling: AppHeader is shown to authenticated users so
 * they keep their nav, but anonymous viewers also see it (it gracefully
 * falls back to the Sign-in CTA).
 */
export function Templates() {
  const { state } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<TemplateSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cloning, setCloning] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.templates
      .list()
      .then((res) => {
        if (cancelled) return;
        setData(res.templates);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Couldn't load the library. Try again in a moment.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isAuthed = state.status === 'authenticated';
  const canClone =
    isAuthed &&
    state.profile.primaryOrgId !== null &&
    meetsMinRole(state.profile.roles, state.profile.primaryOrgId ?? undefined, 'instructor');

  async function clone(templateId: string) {
    if (state.status !== 'authenticated' || cloning) return;
    setCloning(templateId);
    setError(null);
    try {
      const res = await api.templates.clone(state.token, templateId);
      // Need the new course's org slug to build the URL. The cloner's
      // primary org is the destination — pull its slug from auth state.
      // (Workspace also lists it.) Fallback: dump them on /workspace.
      navigate('/workspace');
      // Light hint — the new draft will appear in their workspace list.
      // A future polish would resolve org slug + jump straight to it.
      void res;
    } catch (e) {
      if (e instanceof ApiError && e.code === 'forbidden') {
        setError(
          'You need an instructor role to clone templates. Sign up as a Solo teacher to get one.',
        );
      } else if (e instanceof ApiError && e.code === 'no_org_context') {
        setError(
          'Cloning needs an org context. If you signed up as a student, switch to a Solo teacher account.',
        );
      } else {
        setError("Couldn't clone right now. Try again in a moment.");
      }
    } finally {
      setCloning(null);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[hsl(220,40%,98%)] via-background to-secondary/40">
      {isAuthed ? (
        <AppHeader />
      ) : (
        <header className="border-b border-border bg-background/85 backdrop-blur">
          <div className="container flex h-14 items-center justify-between gap-4">
            <Link to="/" className="flex items-center" aria-label="EduBorg home">
              <img
                src="/brand/eduborg-wordmark.png"
                alt="EduBorg"
                width={120}
                height={32}
                className="h-8 w-auto"
              />
            </Link>
            <Link
              to="/auth/sign-in"
              className="text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              Sign in
            </Link>
          </div>
        </header>
      )}

      <main className="container max-w-4xl space-y-8 py-10">
        <header className="space-y-2">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">EduBorg library</p>
          <h1 className="font-display text-3xl font-bold tracking-tight">Course templates</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Published course skeletons from EduBorg instructors. Clone one into your workspace, then
            customize freely. Your cloned copy starts as a draft, gets a new join code, and doesn't
            carry any of the original course's students or grades.
          </p>
        </header>

        {error && (
          <div
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}

        {data === null ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-accent" aria-hidden />
          </div>
        ) : data.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-muted-foreground">
            No published templates yet. Once instructors start publishing their courses, they'll
            appear here.
          </p>
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2">
            {data.map((t) => (
              <li
                key={t.id}
                className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5 shadow-sm"
              >
                <div className="space-y-1">
                  <h2 className="flex items-center gap-2 font-display text-base font-semibold">
                    <BookOpen className="h-4 w-4 text-accent" aria-hidden /> {t.title}
                  </h2>
                  {t.description && (
                    <p className="text-sm text-muted-foreground">{t.description}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    By{' '}
                    <Link
                      to={`/space/${t.orgSlug}`}
                      className="font-medium text-foreground hover:underline"
                    >
                      {t.orgName}
                    </Link>
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/space/${t.orgSlug}`}
                    className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-secondary"
                  >
                    Preview <ArrowRight className="h-3 w-3" aria-hidden />
                  </Link>
                  {canClone ? (
                    <button
                      type="button"
                      onClick={() => clone(t.id)}
                      disabled={cloning !== null}
                      className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                    >
                      {cloning === t.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <CloneIcon className="h-3 w-3" />
                      )}
                      {cloning === t.id ? 'Cloning…' : 'Clone'}
                    </button>
                  ) : isAuthed ? (
                    <span className="text-xs text-muted-foreground">Instructors only</span>
                  ) : (
                    <Link
                      to="/auth/sign-up"
                      className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                    >
                      Sign up to clone
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
